import {
	GetEmailDiscoveredUrlUsingDNSandDHCPParams,
	ProvidededNativeFunctions,
	StartCustomPortalFlowParams,
	SetAddressDiscoveredStoreUrlsParams,
	ValidateCertChainAndPayloadParams,
	SetAddressDiscoveredStoreUrlsV2Params,
	StartCloudStoreFlowParams,
	StartLegacyFlowParams,
	AddSelectedAccountParams,
	StartOnPremFlowParams,
	FetchGACSSettingsV2Params,
	GACSHybridStoreInfo,
	GetAppConfigDiscoveryDocumentResponse,
	GetAppConfigDiscoveryDocumentParams,
} from './NativeInterface';
import { HttpRequest, HttpResponseJson } from '../../utils/http/Http';
import { NativeAccount } from '../core/TypeDefinitions';
import { AccountType } from '../Constants';
import { GACSSettingEndPoint } from '../core/AppConfigService/AppConfigService';
import { AotCategoryType } from '../../utils/Constants';
import { NativeBridgeMapper } from './NativeBridgeMapper';
import { ResponseJSON } from '../../Common/BridgeInterface';

interface Request {
	id: string;
	params: {
		[key: string]: any;
	};
}
export abstract class NativeBridgeBase
	extends NativeBridgeMapper
	implements ProvidededNativeFunctions
{
	//#region constructor
	constructor() {
		super();
		this.setFunctionMap({
			startAddAccountFlow: this.startAddAccountFlow,
			switchView: this.switchView,
			startRefreshFlow: this.startRefreshFlow,
			addAccountWithoutUI: this.addAccountWithoutUI,
			startAccountMigrationFlow: this.startAccountMigrationFlow,
			getCapabilities: this.getCapabilities,
			performGACSDiscovery: this.performGACSDiscovery,
		});
	}

	//#region call functions
	private startRefreshFlow = async function (req: Request): Promise<void> {
		window.startRefreshFlow(req.id, req.params.discoveryURL, req.params.allowCredUi);
	};
	private addAccountWithoutUI = async function (req: Request): Promise<void> {
		window.addAccountWithoutUI(req.id, JSON.stringify(req.params));
	};
	private getCapabilities = function (req: Request): void {
		window.getCapabilities(req.id);
	};

	private performGACSDiscovery({ id, params }: Request): void {
		window.performGACSDiscovery(id, JSON.stringify(params));
	}
	//#endregion

	//#region events functions
	private startAddAccountFlow = function (): void {
		window.startAddAccountFlow();
	};

	private switchView = function (req: Request): void {
		const viewToSwitch = req.params.viewToSwitch;
		window.switchView(viewToSwitch);
	};

	private startAccountMigrationFlow = function (req: Request): void {
		const skipCount = req.params.skipCount;
		window.startAccountMigrationFlow(skipCount);
	};
	//#endregion

	abstract fetch(httpRequest: HttpRequest): Promise<HttpResponseJson>;
	abstract writeTrace(
		module: string,
		trace: string,
		level?: string,
		aotCategory?: AotCategoryType
	): void;
	abstract accountConfigReady(): void;
	abstract onWebUIReadyToLoad(webUI: string, webUIAuthentication: string): void;
	abstract startCloudStoreFlow(params: StartCloudStoreFlowParams): Promise<{}>;
	abstract startOnPremStoreFlow(params: StartOnPremFlowParams): Promise<{}>;
	abstract startLegacyStoreFlow(
		params: StartLegacyFlowParams
	): Promise<{ accounts: Partial<NativeAccount>[] }>;
	abstract startCustomPortalFlow(params: StartCustomPortalFlowParams): Promise<{}>;
	abstract getAccounts(): Promise<{ accounts: NativeAccount[] }>;
	abstract getEmailDiscoveredUrlUsingDNSandDHCP(
		params: GetEmailDiscoveredUrlUsingDNSandDHCPParams
	): Promise<{ discoveredUrl: string }>;
	abstract onRefreshComplete(id: string, payload: ResponseJSON): void;
	abstract showAccounts(): void;
	abstract sendAction(storeID: string): void;
	abstract doTryAgain(): void;
	abstract closeAddAccountView(): void;
	abstract setAddressDiscoveredStoreUrls(
		params: SetAddressDiscoveredStoreUrlsParams
	): void;
	abstract setAddressDiscoveredStoreUrlsV2(
		params: SetAddressDiscoveredStoreUrlsV2Params
	): void;
	abstract fetchGACSSettingsV2(params: FetchGACSSettingsV2Params): void;
	abstract onAddAccountWithoutUIComplete(id: string, payload: ResponseJSON): void;
	abstract addSelectedAccount(jsonParams: AddSelectedAccountParams): Promise<{}>;
	abstract shouldAddAccountProceed(params: string): Promise<{ status: boolean }>;
	abstract continueMigration(): void;
	abstract skipMigration(): void;
	abstract validateConfigSettings(
		type: AccountType,
		address: string,
		storeUrls: string[],
		gacsSettingsEndpoints: Record<string, GACSSettingEndPoint>
	): Promise<{ selectedStoreUrl: string }>;
	abstract validatePublicCertChain(certificate: string): Promise<{}>;
	abstract validateCertChainAndPayload(
		params: ValidateCertChainAndPayloadParams
	): Promise<{ isValid: boolean }>;
	abstract performanceMeasure(eventName: string, perfTimeStamp: string): void;
	abstract onGetCapabilitiesComplete(id: string, payload: ResponseJSON): void;
	abstract displayCertificate(params: string): void;
	abstract openLink(params: string): void;
	abstract switchNativeWebView(params: string): void;
	abstract isSwitchViewAllowedOnAuthUI(): Promise<{ status: boolean }>;
	abstract onPerformGACSDiscoveryComplete(
		callbackId: string,
		jsonPayload: ResponseJSON
	): void;
	abstract getAllGACSHybridStoresInfo(): Promise<{ stores: GACSHybridStoreInfo[] }>;
	abstract onGetNetworkLocationFromURLComplete(id: string, payload: ResponseJSON): void;
	abstract getAppConfigDiscoveryDocument(
		jsonParams: GetAppConfigDiscoveryDocumentParams
	): Promise<GetAppConfigDiscoveryDocumentResponse>;
}
